import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import {
    DataGrid,
    GridColDef,
    GridToolbarContainer,
    GridSlots,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
} from '@mui/x-data-grid';
import { SystemConfiguration } from '../interfaces';
import { transformKeyToTitle } from '../utils/transformKeyToTitle';
import Button from '@mui/material/Button';
import { Add as AddIcon } from '@mui/icons-material';
import { AddSystemConfigurationDialog } from './AddSystemConfigurationDialog';
import RefreshIcon from '@mui/icons-material/Refresh';
import { loadSystemConfigurations } from '../store/systemConfigurations.reducer';
import { useAppDispatch } from '../store/hooks';
import { useManageGrid } from '../hooks/useManageGrid';
import { CopyTextCell } from './GridCells/CopyTextCell';
import { BooleanFlagCell } from './GridCells/BooleanFlagCell';

interface DataGridComponentProps {
    configurations: SystemConfiguration[];
}

function Toolbar() {
    const dispatch = useAppDispatch();

    const [addConfigurationDialogOpen, setAddConfigurationDialogOpen] = useState(false);

    const onAdd = () => {
        setAddConfigurationDialogOpen(true);
    };

    const onReload = () => {
        dispatch(loadSystemConfigurations());
    };

    return (
        <>
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <Button startIcon={<RefreshIcon />} color="primary" onClick={onReload}>
                    Reload
                </Button>
                <Button startIcon={<AddIcon />} color="primary" onClick={onAdd}>
                    Add
                </Button>
            </GridToolbarContainer>
            {addConfigurationDialogOpen && (
                <AddSystemConfigurationDialog
                    open={addConfigurationDialogOpen}
                    onClose={() => setAddConfigurationDialogOpen(false)}
                />
            )}
        </>
    );
}

export const SystemConfigurationsTableGrid = ({ configurations }: DataGridComponentProps) => {
    const {
        sortModel,
        columnsConfig,
        columnsWidth,
        gridFilterModel,
        handleColumnVisibilityChange,
        handleColumnWidthChange,
        handleSortModelChange,
        handleFilterModelChange,
        onCellKeyDown,
        gridStateSynced,
    } = useManageGrid('systemConfiguration');

    const columns: GridColDef[] = useMemo(() => {
        const textCols: GridColDef[] = [
            'systemVersion',
            'applicationVersion',
            'mapVersion',
            'containerVersion',
            'osVersion',
        ].map((key) => ({
            field: key,
            headerName: transformKeyToTitle(key),
            width: columnsWidth[key] || 200,
            align: 'left' as const,
            headerAlign: 'left' as const,
            sortable: true,
            filterable: true,
            renderCell: CopyTextCell,
        }));

        const odometryOnCol: GridColDef = {
            field: 'odometryOn',
            headerName: transformKeyToTitle('odometryOn'),
            width: columnsWidth['odometryOn'] || 200,
            align: 'left',
            headerAlign: 'left',
            sortable: true,
            filterable: true,
            renderCell: BooleanFlagCell,
        };

        return [...textCols, odometryOnCol];
    }, [columnsWidth]);

    const rows = configurations.map((c) => {
        const row: any = {
            systemVersion: c.systemVersion,
            ...c.configuration,
            id: c.systemVersion,
        };

        return row;
    });

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {gridStateSynced && (
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <DataGrid
                        sx={{ height: '100%' }}
                        rows={rows}
                        columns={columns}
                        slots={{
                            toolbar: Toolbar as GridSlots['toolbar'],
                        }}
                        disableRowSelectionOnClick
                        sortModel={sortModel}
                        filterModel={gridFilterModel}
                        onSortModelChange={handleSortModelChange}
                        onColumnWidthChange={handleColumnWidthChange}
                        onColumnVisibilityModelChange={handleColumnVisibilityChange}
                        onFilterModelChange={handleFilterModelChange}
                        columnVisibilityModel={columnsConfig}
                        onCellKeyDown={onCellKeyDown}
                    />
                </Box>
            )}
        </Box>
    );
};
